import { useState, useCallback, useEffect } from 'react';
import useLinkedStudiesApi from '../../api/requests/useLinkedStudiesApi';

/**
 * Feature hook for selecting an Approved Parent Request when creating a sub-workflow request.
 *
 * @param {number|string} workflowVersionId
 */
const useParentRequestSelection = (workflowVersionId) => {
  const { getEligibleParentRequests } = useLinkedStudiesApi();
  const [eligibleParents, setEligibleParents] = useState([]);
  const [selectedParentRequestIds, setSelectedParentRequestIds] = useState([]);
  const [requiresParentRequest, setRequiresParentRequest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEligibleParents = useCallback(async () => {
    if (!workflowVersionId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getEligibleParentRequests(workflowVersionId, {
        page: 1,
        limit: 100,
      });
      setEligibleParents(data.eligibleParents || data.data || []);
      setRequiresParentRequest(Boolean(data.requiresParentRequest));
    } catch (err) {
      console.error('Error fetching eligible parent requests:', err);
      setError(err.message || 'Failed to fetch eligible parent requests');
      setEligibleParents([]);
      setRequiresParentRequest(false);
    } finally {
      setLoading(false);
    }
  }, [workflowVersionId, getEligibleParentRequests]);

  useEffect(() => {
    fetchEligibleParents();
  }, [fetchEligibleParents]);

  return {
    eligibleParents,
    selectedParentRequestIds,
    setSelectedParentRequestIds,
    requiresParentRequest,
    loading,
    error,
    refetch: fetchEligibleParents,
  };
};

export default useParentRequestSelection;
